import React, { useEffect, useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LabelList } from 'recharts';
import { XMarkIcon } from '@heroicons/react/24/outline';
import api from '../../../../api';
import { useAuth } from '../../../Layouts/context/Authcontext';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';

const ClientProductivityChart = ({ filters }) => {
  const { accessToken } = useAuth();
  const [chartData, setChartData] = useState([]);
  const [statistics, setStatistics] = useState({
    average: 0,
    maxClient: null,
    minClient: null,
  });
  const [focusMode, setFocusMode] = useState(false);
  const [clientProductivityLoading, setClientProductivityLoading] = useState(true);

  const buildParams = (filters) => {
    const params = {};
    Object.keys(filters).forEach((key) => {
      if (filters[key]) {
        params[key] = filters[key];
      }
    });
    return params;
  };

  useEffect(() => {
    const fetchClientProductivity = async () => {
      try {
        setClientProductivityLoading(true);
        const baseUrl = getRoundRobinBaseUrl();
        const response = await api.get(`${baseUrl}/superAdminDashboard/getClientWiseBiometricProductivity`, {
          headers: { Authorization: `Bearer ${accessToken}` },
          params: buildParams(filters),
        });

        if (response.data && response.data.success) {
          const apiData = response.data.data || [];

          const mappedData = apiData.map((item) => ({
            client: item.clientId,
            productivity: parseFloat(item.biometricPercentage.replace('%', '')) || 0,
          }));

          const total = mappedData.reduce((sum, item) => sum + item.productivity, 0);
          const average = mappedData.length > 0 ? (total / mappedData.length).toFixed(2) : 0;
          const maxClient = mappedData.length > 0 ? mappedData.reduce((a, b) => (a.productivity > b.productivity ? a : b)) : null;
          const minClient = mappedData.length > 0 ? mappedData.reduce((a, b) => (a.productivity < b.productivity ? a : b)) : null;

          setChartData(mappedData);
          setStatistics({
            average,
            maxClient,
            minClient,
          });
        }
      } catch (error) {
        console.error('Error fetching client productivity:', error);
      } finally {
        setClientProductivityLoading(false);
      }
    };

    if (filters) {
      fetchClientProductivity();
    }
  }, [accessToken, filters]);

  const ChartContent = () => {
    if (!chartData || chartData.length === 0) {
      return <div className="text-center py-12 text-gray-500">No data available</div>;
    }

    const maxY = Math.max(...chartData.map((item) => item.productivity), 100);

    return (
      <ResponsiveContainer>
        <AreaChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 50 }}>
          <defs>
            <linearGradient id="colorProd" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#1A2B5C" stopOpacity={0.8} />
              <stop offset="95%" stopColor="#1A2B5C" stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="client"
            interval={0}
            angle={-45}
            textAnchor="end"
            height={100}
            tick={{ fontSize: 12 }}
            label={{
              value: 'Client',
              position: 'Bottom',
              offset: -5,
              dy: 90,
              style: { fontWeight: 'bold', fill: '#333', fontSize: 14 },
            }}
            padding={{ left: 30, right: 30 }} // Add horizontal padding
          />

          <YAxis
            label={{
              value: 'Productivity %',
              angle: -90,
              position: 'insideLeft',
              offset: 10,
              style: { fontWeight: 'bold', fill: '#333', fontSize: 14 },
            }}
            domain={[0, maxY]}
          />
          <Tooltip formatter={(value) => `${value.toFixed(2)}%`} />
          <Area type="monotone" dataKey="productivity" stroke="#1A2B5C" fillOpacity={1} fill="url(#colorProd)" strokeWidth={3}>
            <LabelList
              dataKey="productivity"
              position="top"
              formatter={(value) => `${value.toFixed(2)}%`} // REMOVE this line
              // Replace with custom content:
              content={({ x, y, value }) => (
                <text x={x} y={y - 10} fill="#1A2B5C" fontSize={12} fontWeight="bold" textAnchor="middle">
                  {`${value.toFixed(2)}%`}
                </text>
              )}
            />
          </Area>
        </AreaChart>
      </ResponsiveContainer>
    );
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-md p-6 mt-10 relative">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-800">Client Productivity</h2>
          <button
            onClick={() => setFocusMode(true)}
            className="px-4 py-1.5 text-sm rounded-md bg-[#1A2B5C] text-white hover:bg-[#EE1D23] transition"
          >
            Focus Mode
          </button>
        </div>

        {clientProductivityLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-20 w-20 border-t-4 border-blue-500"></div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="p-4 rounded-lg text-center shadow" style={{ backgroundColor: '#F0F4FA' }}>
                <p className="text-sm" style={{ color: '#808080' }}>
                  Average Productivity
                </p>
                <p className="text-2xl font-bold" style={{ color: '#1A2B5C' }}>
                  {statistics.average ?? '--'}%
                </p>
              </div>
              <div className="p-4 rounded-lg text-center shadow" style={{ backgroundColor: '#EAF4F4' }}>
                <p className="text-sm" style={{ color: '#808080' }}>
                  Highest Productivity
                </p>
                <p className="text-lg font-medium" style={{ color: '#1A2B5C' }}>
                  {statistics.maxClient?.client ?? '--'}
                </p>
                <p className="text-xl font-bold" style={{ color: '#1A2B5C' }}>
                  {statistics.maxClient?.productivity ?? '--'}%
                </p>
              </div>
              <div className="p-4 rounded-lg text-center shadow" style={{ backgroundColor: '#FDEBEB' }}>
                <p className="text-sm" style={{ color: '#808080' }}>
                  Lowest Productivity
                </p>
                <p className="text-lg font-medium" style={{ color: '#EE1D23' }}>
                  {statistics.minClient?.client ?? '--'}
                </p>
                <p className="text-xl font-bold" style={{ color: '#EE1D23' }}>
                  {statistics.minClient?.productivity ?? '--'}%
                </p>
              </div>
            </div>

            <div style={{ width: '100%', height: 400 }}>
              <ChartContent />
            </div>
          </>
        )}
      </div>

      {focusMode && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="relative w-screen min-h-screen bg-white rounded-3xl p-8 shadow-2xl overflow-y-auto">
            <button
              onClick={() => setFocusMode(false)}
              className="absolute top-4 right-4 bg-white rounded-full shadow p-2 hover:text-[#EE1D23] transition"
              title="Exit Focus Mode"
            >
              <XMarkIcon className="w-5 h-5" />
            </button>

            <h2 className="text-2xl font-bold mb-6 text-center text-[#1A2B5C]">Client Productivity (Focus Mode)</h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="p-4 rounded-lg text-center shadow" style={{ backgroundColor: '#F0F4FA' }}>
                <p className="text-sm" style={{ color: '#808080' }}>
                  Average Productivity
                </p>
                <p className="text-2xl font-bold" style={{ color: '#1A2B5C' }}>
                  {statistics.average ?? '--'}%
                </p>
              </div>
              <div className="p-4 rounded-lg text-center shadow" style={{ backgroundColor: '#EAF4F4' }}>
                <p className="text-sm" style={{ color: '#808080' }}>
                  Highest Productivity
                </p>
                <p className="text-lg font-medium" style={{ color: '#1A2B5C' }}>
                  {statistics.maxClient?.client ?? '--'}
                </p>
                <p className="text-xl font-bold" style={{ color: '#1A2B5C' }}>
                  {statistics.maxClient?.productivity ?? '--'}%
                </p>
              </div>
              <div className="p-4 rounded-lg text-center shadow" style={{ backgroundColor: '#FDEBEB' }}>
                <p className="text-sm" style={{ color: '#808080' }}>
                  Lowest Productivity
                </p>
                <p className="text-lg font-medium" style={{ color: '#EE1D23' }}>
                  {statistics.minClient?.client ?? '--'}
                </p>
                <p className="text-xl font-bold" style={{ color: '#EE1D23' }}>
                  {statistics.minClient?.productivity ?? '--'}%
                </p>
              </div>
            </div>

            <div style={{ width: '100%', height: 500 }}>
              <ChartContent />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientProductivityChart;
